import React, { useEffect, useRef, useState } from "react";

import styles from "./PillDropdown.module.scss";

export interface PillOption {
  value: string;
  label: string;
}

interface Props {
  /** Shown on the pill when nothing is chosen, e.g. "Coach". */
  label: string;
  value: string;
  options: PillOption[];
  onChange: (value: string) => void;
  testId?: string;
}

/**
 * A pill that opens a menu of its own, in place of a native `<select>`. The
 * native control renders as the operating system's menu — grey, system-styled,
 * nothing like the frame — and the app's form styling stretches it to the full
 * width besides.
 */
const PillDropdown: React.FC<Props> = ({
  label,
  value,
  options,
  onChange,
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Clicking anywhere else closes it, which a native select does for free.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDocumentClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", onDocumentClick);
    return () => document.removeEventListener("click", onDocumentClick);
  }, [open]);

  const selected = options.find((option) => option.value === value);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={selected ? styles.trigger_on : styles.trigger}
        onClick={() => setOpen((isOpen) => !isOpen)}
        aria-expanded={open}
        data-testid={testId}
      >
        <span className={styles.trigger_label}>{selected?.label || label}</span>
        <i
          className={`ico ico-chevron ${styles.chevron} ${
            open ? styles.chevron_open : ""
          }`}
        />
      </button>

      {open && (
        <div className={styles.menu} role="listbox">
          {/* The entry that clears the filter, carrying the pill's own label.
              It is an `option` like the rest: "any duration" is one of the
              choices this listbox offers, and a listbox with a plain button
              among its options is invalid ARIA — a screen reader is told to
              expect options and finds something it has no place for.

              It was left a button so e2e could tell it from the values by role.
              That is what `data-testid` is for; the page object reads this
              instead, and `filterOptions` excludes it by the same hook. */}
          <button
            type="button"
            role="option"
            aria-selected={value === "all"}
            data-testid="filter-clear"
            className={value === "all" ? styles.item_selected : styles.item}
            onClick={() => {
              onChange("all");
              setOpen(false);
            }}
          >
            {label}
          </button>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={
                option.value === value ? styles.item_selected : styles.item
              }
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PillDropdown;
